import { Button } from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import { ClipboardCopy, CopyCheck } from 'lucide-react';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';

import styles from './styles.module.css';
import type { ErrorProps } from './types';

const ErrorMessage: FC<ErrorProps> = ({ error, hasCopy = true, level = 'error' }) => {
  const { t } = useTranslation('errors');
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const hasCopiedText = Boolean(copiedText);

  return (
    <Stack className={styles.error}>
      <Stack
        alignItems="center"
        className={styles.titleContainer}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Stack alignItems="center" gap={3} orientation="horizontal">
          <Text as="span" className={clsx(styles.indicator, styles[`indicator${level}`])}>
            &nbsp;
          </Text>

          <Text as="h4" className={clsx(styles.title, styles.spacing, getTextClassName('lg'))}>
            {level === 'error' ? t('levels.err') : t('levels.warn')}:{' '}
            {error?.message && <Text as="span">&nbsp;{error.message}</Text>}
          </Text>
        </Stack>

        {hasCopy && (
          <Tooltip
            content={hasCopiedText ? t('copySuccess') : t('copyTooltip')}
            placement="top-end"
            trigger={
              <Button
                data-testid="btn-copy-error"
                isDisabled={hasCopiedText}
                onPress={() => copyToClipboard(String(error?.stack || error?.message || error?.cause || error?.name))}
                visual="unstyled"
              >
                {hasCopiedText ? <CopyCheck /> : <ClipboardCopy />}
              </Button>
            }
          />
        )}
      </Stack>

      {error?.stack && (
        <Text as="pre" className={clsx(styles.pre, styles.spacing, getTextClassName('lg'))}>
          {error.stack}
        </Text>
      )}
    </Stack>
  );
};

export default ErrorMessage;
